import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Plane, Mail, Lock, User, Phone, MapPin, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

// Liste des pays avec leurs codes téléphoniques
const phoneCountries = [
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'US', name: 'États-Unis', dialCode: '+1', flag: '🇺🇸' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪' },
  { code: 'CH', name: 'Suisse', dialCode: '+41', flag: '🇨🇭' },
  { code: 'CM', name: 'Cameroun', dialCode: '+237', flag: '🇨🇲' },
  { code: 'SN', name: 'Sénégal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'CI', name: 'Côte d\'Ivoire', dialCode: '+225', flag: '🇨🇮' },
  { code: 'ML', name: 'Mali', dialCode: '+223', flag: '🇲🇱' },
  { code: 'MA', name: 'Maroc', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216', flag: '🇹🇳' },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪' },
  { code: 'ES', name: 'Espagne', dialCode: '+34', flag: '🇪🇸' },
  { code: 'IT', name: 'Italie', dialCode: '+39', flag: '🇮🇹' },
  { code: 'GA', name: 'Gabon', dialCode: '+241', flag: '🇬🇦' },
  { code: 'CG', name: 'Congo', dialCode: '+242', flag: '🇨🇬' },
  { code: 'CD', name: 'RD Congo', dialCode: '+243', flag: '🇨🇩' },
  { code: 'BF', name: 'Burkina Faso', dialCode: '+226', flag: '🇧🇫' },
  { code: 'NE', name: 'Niger', dialCode: '+227', flag: '🇳🇪' },
  { code: 'TG', name: 'Togo', dialCode: '+228', flag: '🇹🇬' },
  { code: 'BJ', name: 'Bénin', dialCode: '+229', flag: '🇧🇯' },
];

// Liste des pays de résidence (pour le champ pays)
const countries = [
  'Canada', 'États-Unis', 'France', 'Belgique', 'Suisse', 
  'Cameroun', 'Sénégal', 'Côte d\'Ivoire', 'Mali', 'Maroc', 
  'Algérie', 'Tunisie', 'Royaume-Uni', 'Allemagne', 'Espagne', 
  'Italie', 'Gabon', 'Congo', 'RD Congo', 'Autre'
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState('CA'); // Default to Canada
  const [phoneNumber, setPhoneNumber] = useState('');
  const [form, setForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    country: '',
    address: '',
    data_usage_consent: false,
    good_conduct_consent: false
  });

  // Get selected phone country
  const selectedPhoneCountry = phoneCountries.find(c => c.code === phoneCountryCode);

  // Build full phone number with country code
  const getFullPhoneNumber = () => {
    if (!phoneNumber) return '';
    // Remove any spaces, dashes, or leading zeros
    const cleanNumber = phoneNumber.replace(/[\s-]/g, '').replace(/^0+/, '');
    return `${selectedPhoneCountry?.dialCode || '+1'}${cleanNumber}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validate email format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(form.email)) {
      toast.error('Format d\'email invalide (ex: nom@exemple.com)');
      setLoading(false);
      return;
    }
    
    // Build the full phone number
    const fullPhone = getFullPhoneNumber();
    
    // Validate phone format
    const phoneRegex = /^\+?[0-9]{8,15}$/;
    if (!fullPhone || !phoneRegex.test(fullPhone.replace(/[\s-]/g, ''))) {
      toast.error('Format de téléphone invalide (ex: +14381234567)');
      setLoading(false);
      return;
    }
    
    try {
      const submitData = {
        ...form,
        phone: fullPhone
      };
      
      const result = await register(submitData);
      toast.success('Inscription réussie !');
      
      if (result.email_sent) {
        toast.info('Un code de vérification a été envoyé à votre adresse email', {
          duration: 8000,
          description: 'Vérifiez votre boîte de réception et vos spams.'
        });
      } else if (result.email_code) {
        toast.info(`Code email: ${result.email_code}`, {
          duration: 10000,
          description: 'Email non envoyé - code affiché pour test'
        });
      }
      
      if (result.phone_code) {
        toast.info(`Code SMS: ${result.phone_code}`, {
          duration: 10000,
          description: 'Intégration SMS à venir - code affiché pour test'
        });
      }
      
      // Auto-login
      await login(form.email, form.password);
      const redirect = sessionStorage.getItem('kacos_redirect_after_login');
      sessionStorage.removeItem('kacos_redirect_after_login');
      navigate(redirect || '/hub');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erreur lors de l\'inscription';
      if (detail.includes('existe déjà') || detail.includes('already registered')) {
        toast.error(detail, {
          duration: 10000,
          action: {
            label: 'Se connecter',
            onClick: () => navigate('/login')
          }
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-primary/90 to-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00em0wLTEwYzAtMi4yIDEuOC00IDQtNHM0IDEuOCA0IDQtMS44IDQtNCA0LTQtMS44LTQtNHptMC0xMGMwLTIuMiAxLjgtNCA0LTRzNCAxLjggNCA0LTEuOCA0LTQgNC00LTEuOC00LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20" />
        
        <div className="relative z-10 flex flex-col justify-center p-12 text-foreground">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_60b0b1ea-0b06-47a8-bc3e-0e0d910144d6/artifacts/xd1x3snr_kacos-logo-final-transparent.svg" 
              alt="Kacos Logo" 
              className="h-14 w-auto"
            />
          </Link>
          
          <h1 className="font-heading text-4xl font-bold mb-4">
            Rejoignez Kacos
          </h1>
          <p className="text-lg text-foreground max-w-md">
            Créez votre compte pour commencer à acheter ou vendre des kilos 
            de bagages en toute sécurité.
          </p>
          
          <div className="mt-12 p-6 bg-muted backdrop-blur rounded-2xl">
            <h3 className="font-semibold mb-3">Processus de vérification</h3>
            <ul className="space-y-2 text-sm text-foreground">
              <li>✓ Vérification email et téléphone</li>
              <li>✓ Validation de vos billets d'avion</li>
              <li>✓ Badge "Profil vérifié" visible</li>
              <li>✓ Accès complet à la plateforme</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6" data-testid="register-back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Retour</span>
          </button>
          <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden">
            <img 
              src="https://customer-assets.emergentagent.com/job_60b0b1ea-0b06-47a8-bc3e-0e0d910144d6/artifacts/xd1x3snr_kacos-logo-final-transparent.svg" 
              alt="Kacos Logo" 
              className="h-10 w-auto"
            />
          </Link>

          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader className="px-0">
              <CardTitle className="font-heading text-2xl">Créer un compte</CardTitle>
              <CardDescription>
                Remplissez le formulaire pour vous inscrire
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Prénom</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="first_name"
                        placeholder="Jean"
                        value={form.first_name}
                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        className="pl-10 h-12"
                        required
                        data-testid="register-firstname"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Nom</Label>
                    <Input
                      id="last_name"
                      placeholder="Dupont"
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      className="h-12"
                      required
                      data-testid="register-lastname"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="vous@exemple.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="pl-10 h-12"
                      required
                      data-testid="register-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <div className="flex gap-2">
                    {/* Country Code Selector - native select for mobile reliability */}
                    <div className="relative w-[140px]">
                      <select
                        value={phoneCountryCode}
                        onChange={(e) => setPhoneCountryCode(e.target.value)}
                        className="w-full h-12 rounded-md border border-input bg-transparent px-3 py-2 text-sm appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring"
                        data-testid="phone-country-code"
                      >
                        {phoneCountries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.dialCode} {country.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="h-4 w-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                    
                    {/* Phone Number Input */}
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="514 123 4567"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="pl-10 h-12"
                        required
                        data-testid="register-phone"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Numéro complet : {getFullPhoneNumber() || '-'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Pays de résidence</Label>
                  <div className="relative">
                    <select
                      value={form.country}
                      onChange={(e) => setForm({ ...form, country: e.target.value })}
                      className="w-full h-12 rounded-md border border-input bg-transparent px-3 py-2 text-sm appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring"
                      required
                      data-testid="register-country"
                    >
                      <option value="" disabled>Sélectionnez un pays</option>
                      {countries.map((country) => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="h-4 w-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Adresse (optionnel)</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="address"
                      placeholder="123 Rue Sainte-Catherine, Montréal"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="pl-10 h-12"
                      data-testid="register-address"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="pl-10 pr-10 h-12"
                      required
                      minLength={6}
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  En vous inscrivant, vous acceptez nos{' '}
                  <Link to="/terms" className="text-primary hover:underline">
                    conditions d'utilisation
                  </Link>
                  {' '}et notre disclaimer transport.
                </p>

                {/* Consent Checkboxes */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="data_consent"
                      checked={form.data_usage_consent}
                      onCheckedChange={(checked) => setForm({ ...form, data_usage_consent: checked })}
                      data-testid="data-usage-consent"
                    />
                    <label htmlFor="data_consent" className="text-sm leading-tight cursor-pointer">
                      J'accepte que mes données personnelles soient utilisées conformément à la{' '}
                      <span className="text-primary font-medium">Politique de Confidentialité</span>{' '}
                      pour le traitement de mes commandes, la prévention des fraudes et les communications liées à mon compte.
                    </label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="conduct_consent"
                      checked={form.good_conduct_consent}
                      onCheckedChange={(checked) => setForm({ ...form, good_conduct_consent: checked })}
                      data-testid="good-conduct-consent"
                    />
                    <label htmlFor="conduct_consent" className="text-sm leading-tight cursor-pointer">
                      Je m'engage à respecter les{' '}
                      <span className="text-primary font-medium">règles de bonne conduite</span>{' '}
                      de la plateforme : fournir des informations exactes, ne pas publier de contenus illégaux, 
                      respecter les autres utilisateurs et les décisions de l'administration.
                    </label>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 btn-primary"
                  disabled={loading || !form.country || !form.data_usage_consent || !form.good_conduct_consent}
                  data-testid="register-submit"
                >
                  {loading ? 'Inscription...' : 'Créer mon compte'}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                Déjà un compte ?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Se connecter
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
